import { useCallback } from 'react'
import { stakeFarm } from 'utils/calls'
import BigNumber from 'bignumber.js'
import { DEFAULT_TOKEN_DECIMAL, DEFAULT_GAS_LIMIT } from 'config'
import { BIG_TEN, BIG_ZERO } from 'utils/bigNumber'
import {
  useBuyHandwithHodlx,
  useBuyNFTwithHodl,
  useBuyGemFighter,
  useSousChef,
  useERC721,
  useErc721CollectionContract,
  useStakeNFTFixedApr,
  useSwapGF,
} from 'hooks/useContract'
import getGasPrice from 'utils/getGasPrice'
import addresses from 'config/constants/contracts'
import Pool from 'views/Pool'
import pools from 'config/constants/pools'

let options = {
  gasLimit: 700000,
}

const buyHandWithHoldx = async (sousChefContract, amount) => {
  const gasPrice = getGasPrice()
  return sousChefContract.purchase(amount, {
    ...options,
    gasPrice,
  })
}

const buyHandWithBnb = async (handContract, amount, handPrice) => {
  return handContract.purchase(amount, {
    value: new BigNumber(amount).times(new BigNumber(handPrice).times(BIG_TEN.pow(18))).toString(),
  })
}

const swapGF = async (contract, tokenIds) => {
  const gasPrice = getGasPrice()
  return contract.swapGF(tokenIds, {
    gasPrice,
  })
}

const buyGemFighterWithBnb = async (contract, amount, price) => {
  const gasPrice = getGasPrice()
  return contract.purchase(amount, {
    value: new BigNumber(amount)
      .times(new BigNumber(price).times(BIG_TEN.pow(18)).times(1.001))
      .toFixed(0)
      .toString(),
    gasLimit: 150000 + amount * 150000,
    gasPrice,
  })
}

const buyGemFighterWithHodl = async (sousChefContract, amount) => {
  const gasPrice = getGasPrice()
  const estimate = await sousChefContract.estimateGas.purchaseGF(amount, {
    gasPrice,
  })

  let options = {
    gasLimit: (estimate * 1.1).toFixed(0),
  }

  return sousChefContract.purchaseGF(amount, {
    ...options,
    gasPrice,
  })
}

const buyHandWithHodl = async (sousChefContract, amount) => {
  const gasPrice = getGasPrice()
  const estimate = await sousChefContract.estimateGas.purchaseHand(amount, {
    gasPrice,
  })

  let options = {
    gasLimit: (estimate * 1.1).toFixed(0),
  }

  return sousChefContract.purchaseHand(amount, {
    ...options,
    gasPrice,
  })
}

const buyGemFighterWithHodlx = async (sousChefContract, amount) => {
  let optionsGemfighter = {
    gasLimit: 500000 + amount * 125000,
  }
  const gasPrice = getGasPrice()
  return sousChefContract.purchaseHODLX(amount, {
    ...optionsGemfighter,
    gasPrice,
  })
}

const sousStake = async (sousChefContract, amount, decimals = 18, isNft?: boolean, pool?: any) => {
  const gasPrice = getGasPrice()
  const estimate = await sousChefContract.estimateGas.deposit(
    new BigNumber(amount).times(BIG_TEN.pow(decimals)).toString(),
    {
      ...(pool?.depositFee
        ? {
            value: new BigNumber((await sousChefContract.getFee()).toString())
              .times(1.02)
              .integerValue(BigNumber.ROUND_DOWN)
              .toString(),
          }
        : {}),
      gasPrice,
    },
  )
  return sousChefContract.deposit(new BigNumber(amount).times(BIG_TEN.pow(decimals)).toString(), {
    gasLimit: (estimate * 1.1).toFixed(0),
    ...(pool?.depositFee
      ? {
          value: new BigNumber((await sousChefContract.getFee()).toString())
            .times(1.02)
            .integerValue(BigNumber.ROUND_DOWN)
            .toString(),
        }
      : {}),
    gasPrice,
  })
}

const sousStakeNFT = async (sousChefContract, amount, decimals = 18, isNft?: boolean, pool?: any) => {
  const fee = pool?.depositFee
    ? new BigNumber((await sousChefContract.getFee()).toString()).times(Number(amount))
    : BIG_ZERO
  const maxFee = pool?.depositFee ? new BigNumber((await sousChefContract.maxFee()).toString()) : BIG_ZERO

  const feeToCharge = fee.gt(maxFee) ? maxFee : fee

  const estimate = await sousChefContract.estimateGas.deposit(
    new BigNumber(amount).times(BIG_TEN.pow(decimals)).toString(),
    {
      value: feeToCharge.times(1.02).integerValue().toFixed(),
      // gasPrice,
    },
  )

  return sousChefContract.deposit(new BigNumber(amount).times(BIG_TEN.pow(decimals)).toString(), {
    gasLimit: (estimate * 1.1).toFixed(0),
    value: feeToCharge.times(1.02).integerValue(BigNumber.ROUND_DOWN).toString(),
    // gasPrice,
  })
}

const sousStakeNFTFixedAprWithRange = async (sousChefContract, amount, decimals = 18, pool?: any) => {
  const gasPrice = getGasPrice()

  const fee = pool?.depositFee
    ? new BigNumber((await sousChefContract.getFee()).toString()).times(Number(amount))
    : BIG_ZERO

  const NFTsToStake = pool.userData.gfTokenIds
    .filter((id) => id >= pool.allowedTokenIds.from && id <= pool.allowedTokenIds.to)
    .sort((a, b) => {
      if (Number(a) > Number(b)) {
        return 1
      } else {
        return -1
      }
    })
    .slice(0, amount)

  const estimate = await sousChefContract.estimateGas.deposit(
    new BigNumber(amount).times(BIG_TEN.pow(decimals)).toString(),
    NFTsToStake,
    {
      value: fee.times(1.02).integerValue(BigNumber.ROUND_DOWN).toString(),
      gasPrice,
    },
  )

  return sousChefContract.deposit(new BigNumber(amount).times(BIG_TEN.pow(decimals)).toString(), NFTsToStake, {
    gasLimit: (estimate * 1.1).toFixed(0),
    value: fee.times(1.02).integerValue(BigNumber.ROUND_DOWN).toString(),
    gasPrice,
  })
}

const sousStakeBnb = async (sousChefContract, amount) => {
  const gasPrice = getGasPrice()
  return sousChefContract.deposit(new BigNumber(amount).times(DEFAULT_TOKEN_DECIMAL).toString(), {
    ...options,
    gasPrice,
  })
}

const useStakePool = (sousId: number, isUsingBnb = false) => {
  // const handContract = '0x7E82123bCb6465133D6E9E1Ad94d0115DE041b3D'
  const sousChefContract = useSousChef(sousId)
  let contractABI
  switch (sousId) {
    case 0:
      contractABI = useBuyHandwithHodlx()
      break
    case 5:
      contractABI = useSwapGF()
      break
    case 14:
    case 15:
      contractABI = useBuyNFTwithHodl()
      break
    case 21:
      contractABI = useErc721CollectionContract(addresses.hodlhandsNFT[56])
      break
    case 38:
    case 39:
    case 40:
      contractABI = useBuyGemFighter(addresses.gemfighterNFT[56])
      break
    case 52:
    case 53:
      contractABI = useStakeNFTFixedApr(pools.filter((p) => p.sousId === sousId)[0].contractAddress[56])
      break
    default:
      break
  }

  const handleStake = useCallback(
    async (amount: string, decimals: number, isNft?: boolean, pool?: any, tokenIds?: any[]) => {
      if (pool.isBuyNFT == true) {
        if (pool.contractAddress[56] == addresses.hodlhandsNFT[56]) {
          return buyHandWithBnb(contractABI, amount, pool.earningTokenPrice)
        } else if (pool.sousId === 5) {
          return swapGF(contractABI, tokenIds)
        } else if (pool.sousId === 14) {
          return buyGemFighterWithHodl(contractABI, amount)
        } else if (pool.sousId === 15) {
          return buyHandWithHodl(contractABI, amount)
        } else if (pool.sousId === 38) {
          return buyGemFighterWithBnb(contractABI, amount, pool.earningTokenPrice)
        } else if (pool.sousId === 40) {
          return buyGemFighterWithHodlx(contractABI, amount)
        } else {
          return buyHandWithHoldx(contractABI, amount)
        }
      }

      if (isUsingBnb) {
        return sousStakeBnb(sousChefContract, amount)
      }

      if (pool.sousId === 41 || pool.sousId === 42 || (pool.fixedApr && !pool.hasNFTRange) || pool.isNft) {
        return sousStakeNFT(sousChefContract, amount, decimals, isNft, pool)
      }

      if (pool.fixedApr && pool.hasNFTRange) {
        return sousStakeNFTFixedAprWithRange(contractABI, amount, decimals, pool)
      }

      return sousStake(sousChefContract, amount, decimals, isNft, pool)
    },
    [isUsingBnb, contractABI, sousChefContract, sousId],
  )

  return { onStake: handleStake }
}

export default useStakePool
